import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { combineLatest, Subject, Subscription, takeUntil, take } from 'rxjs';
import { Alarm } from '../../../models/alarm.model';
import { Rule } from '../../../models/rule.model';
import { createDefaultTableState } from '../../../../utils/table.utils';
import { ColumnFilter, Table, TableLazyLoadEvent } from 'primeng/table';
import { cloneDeep } from 'lodash';
import {
  saveTableLazyLoadEvent,
  clearTableLazyLoadEvent,
} from '../../../store/common/app.action';
import { FullTableState } from '../../../store/common/app.reducer';
import {
  selectTableState,
  selectSavedLazyLoadEvent,
} from '../../../store/common/app.selectors';
import { selectFilters } from '../../../store/selectors/rules.selector';
import * as rulesActions from '../../../store/actions/rules.actions';
import {
  selectRules,
  selectRulesError,
  selectRulesLoading,
} from '../../../store/selectors/rules.selector';
import {
  selectPagedRules,
  selectPagedTotal,
  selectRulesPageLoading,
} from '../../../store/selectors/rules.selector';
import {
  getStageLabel,
  getCriticalityLabel,
} from '../../../../utils/enum-label.utils';
import {
  isActivationConditionStale,
  isRuleStale,
} from '../../../../utils/rule.utils';
import { CriticalityLevel } from '../../../../enum/criticality-level.enum';
import { criticalityOptions as CRITICALITY_OPTIONS } from '../../../shared/constants/criticality-option.constans';
import { stageOptions as STAGE_OPTIONS } from '../../../shared/constants/stage-options.constant';
import { loadHealthIndicatorTrendIfNeeded } from '../../../store/actions/rule-result.actions';
import { selectHealthIndicatorTrendData } from '../../../store/selectors/rule-result.selector';
import { Popover } from 'primeng/popover';
import { ruleStateOptions as RULE_STATE_OPTIONS } from '../../../shared/constants/rule-state-options.constans';
import { SelectItem } from 'primeng/api';
import { AlarmStatusOptions as ALARM_STATUS_OPTIONS } from '../../../shared/constants/alarm-status.constants';
import {
  FILTER_VALUE,
  TIME_FRAME,
} from '../../../shared/constants/filters.constants';
import { DEFAULT_OPTION } from '../../../shared/constants/default-options.constans';
import { ChartData } from 'chart.js';
import { RuleEvaluationDatasetConverter } from '../../../../utils/chart/rule-evaluation-dataset-converter.utils';
import { SimpleChartOptions } from '../../../../utils/chart/simple-options.utils';
import { ContextService } from '../../../core/services/context.service';
import { clearPagedRules } from '../../../store/actions/rules.actions';
import { PlantFilterSelectionChange } from '../../../shared/components/plant-filters/plant-filters.component';
import { RuleDialogService } from '../../../shared/components/modify-rule/rule-dialog.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
@Component({
  selector: 'app-rules-overview',
  templateUrl: './rules-overview.component.html',
  styleUrl: './rules-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RulesOverviewComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  constructor(
    private store: Store,
    private router: Router,
    private contextService: ContextService,
    private ruleDialogService: RuleDialogService,
  ) {}

  @ViewChild('op') overlayPanel!: Popover;
  @ViewChild('criteriatable') table!: Table;

  criticalityOptions: SelectItem[] = [
    ...DEFAULT_OPTION,
    ...CRITICALITY_OPTIONS,
  ];
  stageOptions: SelectItem[] = STAGE_OPTIONS;
  ruleStateOptions: SelectItem[] = RULE_STATE_OPTIONS;
  loading$ = this.store.select(selectRulesPageLoading);
  error$ = this.store.select(selectRulesError);
  data: Alarm[] = [];
  rules: Rule[] = [];
  isLoading: boolean = true;
  pickedDate: Date | null = null;
  private lastMouseEvent!: MouseEvent;
  private readonly offsetX = 5;
  panelVisible = false;
  private destroy$ = new Subject<void>();
  selectedCriticality: string = FILTER_VALUE.ALL;
  selectedPlant: string = FILTER_VALUE.ALL;
  selectedPlannerGroups: string[] = [];
  selectedWorkCenters: string[] = [];
  selectedStage: string = FILTER_VALUE.ALL;
  selectedState: string = FILTER_VALUE.HEALTHY;
  selectedStatus: string = FILTER_VALUE.ALL;
  alarmStatusOptions: SelectItem[] = ALARM_STATUS_OPTIONS;
  showActiveRules: boolean = true;
  private subscriptions = new Subscription();
  fullTableState: FullTableState = createDefaultTableState();
  rows: number = 10;
  first: number = 0;
  pagedRules: Rule[] = [];
  totalRecords: number = 0;
  getStageLabel = getStageLabel;
  getCriticalityLabel = getCriticalityLabel;
  isActivationConditionStale = isActivationConditionStale;
  isRuleStale = isRuleStale;
  CriticalityLevel = CriticalityLevel;
  trendChartData: ChartData<'line'> | undefined;
  trendChartOptions: any;
  today = new Date();
  failCount: number = 0;
  alarmCount: number = 0;
  lastMouse?: MouseEvent;
  lastRowEl?: HTMLElement;
  rowGapY = 0;
  private ro?: ResizeObserver;
  chartFilterPayload: any;

  private readonly componentName = 'rules-overview';
  private stateRestored = false;
  private initialLoadComplete = false;
  private dialogRef?: DynamicDialogRef | null;
  journalDigestVisible = false;

  get showHealthIndicator(): boolean {
    return this.contextService.state.showHealthIndicator;
  }

  set showHealthIndicator(value: boolean) {
    this.contextService.setShowHealthIndicator(value);
  }

  ngOnInit(): void {
    // Restore plant-based selections from context service
    this.selectedPlant = this.contextService.getSelectedPlant();
    this.selectedPlannerGroups = this.contextService.getSelectedPlannerGroups();
    this.selectedWorkCenters = this.contextService.getSelectedWorkCenters();

    this.restoreSavedTableState().then(() => {
      this.stateRestored = true;
      // Sync filters to fullTableState regardless of saved state
      this.syncFiltersToTableState();
      // Initialize chart filter payload after state is restored
      this.updateChartFilterPayload();
    });

    this.store
      .select(selectPagedRules)
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.pagedRules = items as Rule[];
      });

    this.store
      .select(selectPagedTotal)
      .pipe(takeUntil(this.destroy$))
      .subscribe((total) => {
        this.totalRecords = total as number;
      });

    this.store
      .select(selectFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        if (!filters) return;
      });
  }

  ngAfterViewInit() {
    this.initialLoadComplete = true;
  }

  showAlarmDetails(event: any): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['factory/rule'], {
        queryParams: { id: event.data.uid },
      }),
    );
    window.open(url, '_blank');
  }

  onRowEnter(ev: MouseEvent, alarm: any) {
    this.lastMouseEvent = ev;
    this.lastRowEl = ev.currentTarget as HTMLElement;
    this.overlayPanel.show(ev, this.lastRowEl);
    this.store.dispatch(
      loadHealthIndicatorTrendIfNeeded({
        uid: alarm?.uid,
        range: TIME_FRAME.THREEMONTH,
        alertThreshold: alarm?.alertThreshold ?? 0,
        interpolationInMinutes: Math.max(
          alarm?.piPointInterpolationIntervalInMinutes ?? 60,
          60,
        ),
      }),
    );

    const sub = combineLatest([
      this.store.select(
        selectHealthIndicatorTrendData(alarm?.uid, TIME_FRAME.THREEMONTH),
      ),
    ]).subscribe(([data]) => {
      if (!data) return;
      this.trendChartData = RuleEvaluationDatasetConverter.GenerateChartData(
        TIME_FRAME.MONTH,
        data,
        (alarm?.periodInMinutes ?? 60) * 2,
        alarm?.alertThreshold,
        alarm?.toleranceThreshold,
        alarm?.minAlertThreshold,
        alarm?.minToleranceThreshold,
      );
      this.trendChartOptions = SimpleChartOptions.getOptions(
        'month',
        'MMM dd, HH:mm',
      );
    });

    this.subscriptions.add(sub);
  }

  onRowMove(ev: MouseEvent) {
    this.lastMouse = ev;
    if (this.panelVisible) this.positionNearCursor();
  }

  onRowLeave() {
    this.overlayPanel.hide();
    this.lastRowEl = undefined;
  }

  onFilterSelectionChange(change: PlantFilterSelectionChange) {
    this.selectedPlant = change.plant;
    this.selectedPlannerGroups = change.plannerGroups;
    this.selectedWorkCenters = change.workCenters;

    // Update table filters
    if (this.table && this.table.filters) {
      this.table.filters['plant'] = {
        value: change.plant,
        matchMode: 'contains',
      };
      this.table.filters['plannerGroups'] = {
        value: change.plannerGroups,
        matchMode: 'in',
      };
      this.table.filters['workCenters'] = {
        value: change.workCenters,
        matchMode: 'in',
      };
    }
    if (this.fullTableState.filters) {
      this.fullTableState.filters['plant'] = {
        value: change.plant,
        matchMode: 'contains',
      };
      this.fullTableState.filters['plannerGroups'] = {
        value: change.plannerGroups,
        matchMode: 'in',
      };
      this.fullTableState.filters['workCenters'] = {
        value: change.workCenters,
        matchMode: 'in',
      };
    }

    this.store.dispatch(
      rulesActions.setFilters({
        criticality: this.selectedCriticality,
        alarm: this.selectedStatus,
        period: FILTER_VALUE.TODAY,
        sector: FILTER_VALUE.ALL,
        subSector: FILTER_VALUE.ALL,
        modelType: FILTER_VALUE.ALL,
        factory: FILTER_VALUE.ALL,
        state: this.selectedState,
        stage: this.selectedStage,
      }),
    );

    // Reload data with new filters
    this.loadData(this.fullTableState as TableLazyLoadEvent);
  }

  handleTableFilterChange() {
    this.store.dispatch(
      rulesActions.setFilters({
        criticality: this.selectedCriticality,
        alarm: this.selectedStatus,
        period: FILTER_VALUE.TODAY,
        sector: FILTER_VALUE.ALL,
        subSector: FILTER_VALUE.ALL,
        modelType: FILTER_VALUE.ALL,
        factory: FILTER_VALUE.ALL,
        state: this.selectedState,
        stage: this.selectedStage,
      }),
    );
  }

  filter(value: any, field?: string) {
    if (this.table) {
      this.table.filter(value, field || 'global', 'contains');
    }
  }

  applyFilter(value: any) {
    if (this.table) {
      this.table.filter(value, 'latestAlarmStartDate', 'equals');
    }
  }

  onPopoverHide() {
    this.panelVisible = false;
    this.ro?.disconnect();
    this.ro = undefined;
  }
  onPopoverShow() {
    this.panelVisible = true;
    const panelEl = document.querySelector<HTMLElement>('.trend-popover');
    if (!panelEl) return;
    this.positionNearCursor();
    this.ro = new ResizeObserver(() => {
      requestAnimationFrame(() => this.positionNearCursor());
    });
    this.ro.observe(panelEl);
  }

  loadData(lazyLoadEvent: TableLazyLoadEvent) {
    if (!lazyLoadEvent) return;
    const shouldSaveState = this.stateRestored && this.initialLoadComplete;

    this.fullTableState.first = lazyLoadEvent.first ?? 0;
    this.fullTableState.rows = lazyLoadEvent.rows ?? this.fullTableState.rows;

    if (lazyLoadEvent.sortField !== undefined) {
      this.fullTableState.sortField = lazyLoadEvent.sortField as string;
      this.fullTableState.sortOrder = (lazyLoadEvent.sortOrder as number) ?? 0;
    }

    // Sync component filters to table state before loading data
    this.syncFiltersToTableState();

    if (shouldSaveState) {
      this.store.dispatch(
        saveTableLazyLoadEvent({
          lazyLoadEvent: cloneDeep(this.fullTableState),
          componentName: this.componentName,
        }),
      );
    }

    // Update chart filter payload
    this.updateChartFilterPayload();

    this.store.dispatch(
      rulesActions.loadRulesPage({
        first: this.fullTableState.first ?? 0,
        rows: this.fullTableState.rows ?? 10,
        sortField: this.fullTableState.sortField ?? null,
        sortOrder: this.fullTableState.sortOrder ?? 0,
        filters: cloneDeep(this.fullTableState.filters),
        globalFilter: (lazyLoadEvent as any).globalFilter ?? null,
        showHealthIndicator: this.showHealthIndicator,
      }),
    );
  }

  private updateChartFilterPayload(): void {
    const rawFilters = cloneDeep(this.fullTableState.filters) ?? {};
    rawFilters['isActive'] = {
      value: String(this.showActiveRules),
      matchMode: 'equals',
    };

    this.chartFilterPayload = {
      First: 0,
      Rows: 999999, // Get all for distribution
      SortField: null,
      SortOrder: 0,
      Filters: rawFilters,
      GlobalFilter: null,
      Factory: '',
      IsOrderedByHealthIndicator: false,
    };
  }

  private positionNearCursor() {
    const panelEl = document.querySelector<HTMLElement>('.trend-popover');
    if (!panelEl || !this.lastMouseEvent || !this.lastRowEl) return;

    panelEl.style.position = 'fixed';

    const { clientX } = this.lastMouseEvent;
    const maxLeft = Math.max(8, window.innerWidth - panelEl.offsetWidth - 8);
    const left = Math.min(Math.max(8, clientX + this.offsetX), maxLeft);
    panelEl.style.left = `${left}px`;

    const rowRect = this.lastRowEl.getBoundingClientRect();
    const panelH = panelEl.offsetHeight;
    const gap = this.rowGapY ?? 8;
    const spaceBelow = window.innerHeight - rowRect.bottom;
    const spaceAbove = rowRect.top;

    const shouldFlip = panelH + gap + 8 > spaceBelow && spaceAbove > spaceBelow;

    let top = shouldFlip ? rowRect.top - panelH - gap : rowRect.bottom + gap;

    top = Math.min(Math.max(8, top), window.innerHeight - panelH - 8);
    panelEl.style.top = `${top}px`;

    panelEl.classList.toggle('p-popover-flipped', shouldFlip);
  }

  private restoreSavedTableState(): Promise<void> {
    return new Promise((resolve) => {
      this.store
        .select(selectSavedLazyLoadEvent(this.componentName))
        .pipe(take(1))
        .subscribe((savedLazyLoadEvent) => {
          if (savedLazyLoadEvent) {
            this.fullTableState.first = savedLazyLoadEvent.first ?? 0;
            this.fullTableState.rows = savedLazyLoadEvent.rows ?? 10;
            this.fullTableState.sortField =
              savedLazyLoadEvent.sortField as string;
            this.fullTableState.sortOrder = savedLazyLoadEvent.sortOrder ?? 0;

            if (savedLazyLoadEvent.filters) {
              this.fullTableState.filters = cloneDeep(
                savedLazyLoadEvent.filters,
              ) as any;

              // Extract and restore filter values for UI binding
              Object.keys(savedLazyLoadEvent.filters).forEach((key) => {
                const filterValue = (savedLazyLoadEvent.filters as any)[key];
                const value = filterValue?.value ?? filterValue;
                switch (key) {
                  case 'criticality':
                    this.selectedCriticality = value ?? FILTER_VALUE.ALL;
                    break;
                  case 'plant':
                    this.selectedPlant = value ?? FILTER_VALUE.ALL;
                    break;
                  case 'plannerGroups':
                    this.selectedPlannerGroups = Array.isArray(value)
                      ? value
                      : [];
                    break;
                  case 'workCenters':
                    this.selectedWorkCenters = Array.isArray(value)
                      ? value
                      : [];
                    break;
                  case 'status':
                    this.selectedStatus = value ?? FILTER_VALUE.ALL;
                    break;
                  case 'ruleStage':
                    this.selectedStage = value ?? FILTER_VALUE.ALL;
                    break;
                  case 'state':
                    this.selectedState = value ?? FILTER_VALUE.HEALTHY;
                    break;
                  case 'latestAlarmStartDate':
                    this.pickedDate = value ? value : null;
                    break;
                  case 'isActive':
                    this.showActiveRules = value !== undefined ? value : true;
                    break;
                }
              });

              this.store.dispatch(
                rulesActions.setFilters({
                  criticality: this.selectedCriticality,
                  alarm: this.selectedStatus,
                  period: FILTER_VALUE.TODAY,
                  sector: FILTER_VALUE.ALL,
                  subSector: FILTER_VALUE.ALL,
                  modelType: FILTER_VALUE.ALL,
                  factory: FILTER_VALUE.ALL,
                  state: this.selectedState,
                  stage: this.selectedStage,
                }),
              );
            }

            // Restore plant-based context selections
            this.contextService.setSelectedPlant(this.selectedPlant);
            this.contextService.setSelectedPlannerGroups(
              this.selectedPlannerGroups,
            );
            this.contextService.setSelectedWorkCenters(
              this.selectedWorkCenters,
            );
          } else if (this.showHealthIndicator) {
            this.fullTableState.rows = 50;
          }
          resolve();
        });
    });
  }

  private syncFiltersToTableState(): void {
    // Ensure filters object exists
    if (!this.fullTableState.filters) {
      this.fullTableState.filters = {};
    }

    // Sync plant filter
    if (this.selectedPlant && this.selectedPlant !== FILTER_VALUE.ALL) {
      this.fullTableState.filters['plant'] = {
        value: this.selectedPlant,
        matchMode: 'contains',
      };
    } else {
      delete this.fullTableState.filters['plant'];
    }

    // Sync planner groups filter (multi-select)
    if (this.selectedPlannerGroups && this.selectedPlannerGroups.length > 0) {
      this.fullTableState.filters['plannerGroups'] = {
        value: this.selectedPlannerGroups,
        matchMode: 'in',
      };
    } else {
      delete this.fullTableState.filters['plannerGroups'];
    }

    // Sync work centers filter (multi-select)
    if (this.selectedWorkCenters && this.selectedWorkCenters.length > 0) {
      this.fullTableState.filters['workCenters'] = {
        value: this.selectedWorkCenters,
        matchMode: 'in',
      };
    } else {
      delete this.fullTableState.filters['workCenters'];
    }

    // Sync status filter — always include so the backend receives the value
    this.fullTableState.filters['alarm'] = {
      value: this.selectedStatus ?? FILTER_VALUE.ALL,
      matchMode: 'contains',
    };

    // Sync isActive filter
    this.fullTableState.filters['isActive'] = {
      value: String(this.showActiveRules),
      matchMode: 'equals',
    };
  }

  resetSavedTableState() {
    this.store.dispatch(
      clearTableLazyLoadEvent({ componentName: this.componentName }),
    );
  }

  onActiveRulesToggleChange() {
    this.fullTableState.first = 0;
    this.syncFiltersToTableState();
    this.updateChartFilterPayload();

    this.store.dispatch(
      saveTableLazyLoadEvent({
        lazyLoadEvent: cloneDeep(this.fullTableState),
        componentName: this.componentName,
      }),
    );

    this.store.dispatch(
      rulesActions.loadRulesPage({
        first: 0,
        rows: this.fullTableState.rows ?? 10,
        sortField: this.fullTableState.sortField ?? null,
        sortOrder: this.fullTableState.sortOrder ?? 0,
        filters: cloneDeep(this.fullTableState.filters),
        globalFilter: null,
        showHealthIndicator: this.showHealthIndicator,
        needToReload: true,
      }),
    );
  }

  onHealthIndicatorChange() {
    this.fullTableState.first = 0;
    this.fullTableState.rows = 25;
    if (this.showHealthIndicator) this.fullTableState.rows = 50;

    this.syncFiltersToTableState();

    this.store.dispatch(
      saveTableLazyLoadEvent({
        lazyLoadEvent: cloneDeep(this.fullTableState),
        componentName: this.componentName,
      }),
    );

    this.store.dispatch(
      rulesActions.loadRulesPage({
        first: 0,
        rows: this.fullTableState.rows ?? 10,
        sortField: this.fullTableState.sortField ?? null,
        sortOrder: this.fullTableState.sortOrder ?? 0,
        filters: cloneDeep(this.fullTableState.filters),
        globalFilter: null,
        showHealthIndicator: this.showHealthIndicator,
        needToReload: true,
      }),
    );
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearPagedRules());
    this.destroy$.next();
    this.destroy$.complete();
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  openCreateDialog(): void {
    this.dialogRef = this.ruleDialogService.openCreateRuleDialog(true);
  }

  openSimpleCreateDialog(): void {
    this.dialogRef = this.ruleDialogService.openSimpleCreateRuleDialog();
  }

  openJournalDigest(): void {
    this.journalDigestVisible = true;
  }
}
